import type { Match } from '@/lib/matches';
import { formatMatchDateTime, isMatchLive, isMatchUpcoming } from '@/lib/matches';

interface MatchCardProps {
  match: Match;
  showCompetition?: boolean;
  onClick?: (match: Match) => void;
  useUTC?: boolean;
}

const statusBadge: Record<string, { label: string; class: string }> = {
  LIVE: { label: 'LIVE', class: 'bg-accent/20 text-accent' },
  IN_PLAY: { label: 'LIVE', class: 'bg-accent/20 text-accent' },
  PAUSED: { label: 'LIVE', class: 'bg-accent/20 text-accent' },
  FINISHED: { label: 'FT', class: 'bg-bg-hover text-text-muted' },
  SCHEDULED: { label: '', class: '' },
  TIMED: { label: '', class: '' },
  POSTPONED: { label: 'PPD', class: 'bg-warning/20 text-warning' },
  CANCELLED: { label: 'CANC', class: 'bg-error/20 text-error' },
  SUSPENDED: { label: 'SUSP', class: 'bg-warning/20 text-warning' },
};

function getScoreDisplay(match: Match): string {
  const s = match.score.fullTime;
  if (s.home !== null && s.away !== null) return `${s.home}-${s.away}`;
  return '';
}

export function MatchCard({ match, showCompetition = true, onClick, useUTC = false }: MatchCardProps) {
  const live = isMatchLive(match);
  const upcoming = isMatchUpcoming(match);
  const finished = match.status === 'FINISHED';
  const hasScore = match.score.fullTime.home !== null;
  const badge = statusBadge[match.status] || statusBadge.SCHEDULED;
  const isWarmup = (match as Match & { _warmup?: boolean })._warmup;

  const homeCrest = match.homeTeam.crest;
  const awayCrest = match.awayTeam.crest;

  return (
    <div
      onClick={onClick ? () => onClick(match) : undefined}
      className={`bg-bg-elevated rounded-lg border border-border p-3 flex flex-col gap-2 transition-colors ${
        onClick ? 'cursor-pointer hover:border-accent/40' : ''
      } ${live ? 'border-accent/30 ring-1 ring-accent/10' : ''}`}
    >
      <div className="flex items-center gap-1.5 text-[11px] text-text-muted">
        {showCompetition && match.competition.emblem && (
          <img src={match.competition.emblem} alt="" className="w-3.5 h-3.5 object-contain" />
        )}
        <span className="truncate">{match.competition.name}</span>
        {isWarmup && (
          <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-warning/10 text-warning">
            Friendly
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0 flex items-center gap-2 justify-end">
          {homeCrest && (
            <img src={homeCrest} alt="" className="w-5 h-5 object-contain flex-shrink-0" />
          )}
          <span className="text-sm font-medium truncate">{match.homeTeam.shortName || match.homeTeam.name}</span>
        </div>

        <div className="flex-shrink-0 text-center min-w-[40px]">
          {live && hasScore ? (
            <span className="text-sm font-bold tabular-nums text-accent">{getScoreDisplay(match)}</span>
          ) : finished && hasScore ? (
            <span className="text-sm font-bold tabular-nums text-text-muted">{getScoreDisplay(match)}</span>
          ) : upcoming && match.status === 'TIMED' ? (
            <span className="text-xs tabular-nums text-text-muted">{formatMatchDateTime(match.utcDate, useUTC)}</span>
          ) : (
            <span className="text-xs text-text-muted">vs</span>
          )}
        </div>

        <div className="flex-1 min-w-0 flex items-center gap-2">
          <span className="text-sm font-medium truncate">{match.awayTeam.shortName || match.awayTeam.name}</span>
          {awayCrest && (
            <img src={awayCrest} alt="" className="w-5 h-5 object-contain flex-shrink-0" />
          )}
        </div>
      </div>

      <div className="flex items-center justify-between text-[11px] text-text-muted">
        <span className="flex items-center gap-1">
          {badge.label && (
            <span className={`px-1 py-0.5 rounded text-[10px] font-semibold ${badge.class}`}>
              {badge.label}
            </span>
          )}
          {match.stage && match.stage !== 'REGULAR_SEASON' && (
            <span className="truncate max-w-[100px]">{match.group || ''}</span>
          )}
        </span>
        {live && <span className="text-accent font-semibold">LIVE</span>}
        {upcoming && match.status === 'SCHEDULED' && (
          <span className="text-text-muted">Scheduled</span>
        )}
      </div>
    </div>
  );
}
